import { LoadingFallback } from "@/components/loading-fallback";
import { Button } from "@/components/ui/button";
import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import { Switch } from "@/components/ui/switch";
import { useLiteDashboardStatistics } from "@/hooks/tiktokOrder.hooks";
import {
  ChevronRight,
  DollarSign,
  ExternalLinkIcon,
  Package,
  TrendingDown,
  TrendingUp,
  Users,
  Receipt,
  CreditCard,
  Percent,
  Info,
  InfoIcon,
  Settings,
  ChevronDown,
  Truck,
} from "lucide-react";
import { useState, useMemo, useEffect, useContext } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  ComposedChart,
  Line,
} from "recharts";
import { LiteStatisticPeriod } from "@/types/LiteStatisticPeriod";
import { UserStateContext } from "@/contexts/UserStateProvider";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useLiteSettledDashboardStatistics } from "@/hooks/tiktokSkuSettledPeriod.hooks";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  collectSettledTotals,
  collectTotals,
  combineTotals,
  formatDate,
  formatTooltipValue,
} from "@/lib/utils";
import { ChartTile } from "@/types/ChartTile";
import { ChartTileOverview } from "../../../../components/chart-tile";
import { ChartSettingsPopover } from "../../../../components/chart-settings-popover";

export default function SalesOverviewTile({
  selectedDateRange,
  fromDate,
  toDate,
  onTotalSalesChange,
  onTotalCogsChange,
  headerExtra,
  extraRightHeader,
  overrideTiles
}: {
  selectedDateRange: string;
  fromDate: Date;
  toDate: Date;
  onTotalSalesChange: (totalSales: number) => void;
  onTotalCogsChange: (totalCogs: number) => void;
  headerExtra?: React.ReactNode;
  extraRightHeader?: React.ReactNode;
  overrideTiles?: {
    dataKey: string;
    gridClass: string;
    hide?: boolean;
    color?: string;
    chartColor?: string;
  }[];
}) {
  const { data: liteSettledDashboardStatistics, isLoading: isSettledLoading } =
    useLiteSettledDashboardStatistics(fromDate, toDate, true);
  const { data: liteDashboardStatistics, isLoading } =
    useLiteDashboardStatistics(fromDate, toDate);
  const {
    swapChartsOnDashboard,
    setSwapChartsOnDashboard,
    currency,
    isCumulative,
  } = useContext(UserStateContext);

  const totals = useMemo(
    () =>
      collectTotals(
        liteDashboardStatistics,
        onTotalSalesChange,
        onTotalCogsChange
      ),
    [liteDashboardStatistics]
  );
  const settledTotals = useMemo(
    () => collectSettledTotals(liteSettledDashboardStatistics),
    [liteSettledDashboardStatistics]
  );
  const combinedData = useMemo(
    () =>
      combineTotals(
        liteDashboardStatistics,
        liteSettledDashboardStatistics,
        isCumulative
      ),
    [liteDashboardStatistics, liteSettledDashboardStatistics, isCumulative]
  );

  useEffect(() => {
    console.log("combinedData", combinedData);
  }, [combinedData]);

  const dashboardTiles: ChartTile[] = [
    // {
    //   subtitle: "exc. cancelled orders",
    //   title: "Gross Sales",
    //   tooltip: "Total gross sales before any discounts excludes shipping fees.",
    //   value:
    //     totals && totals.totalGrossSales
    //       ? `${currency.symbol}${totals.totalGrossSales?.toLocaleString(
    //           undefined,
    //           { minimumFractionDigits: 2, maximumFractionDigits: 2 }
    //         )}`
    //       : `${currency.symbol}0.00`,
    //   settledValue:
    //     settledTotals && settledTotals.settledGrossSales
    //       ? `${
    //           currency.symbol
    //         }${settledTotals.settledGrossSales?.toLocaleString(undefined, {
    //           minimumFractionDigits: 2,
    //           maximumFractionDigits: 2,
    //         })}`
    //       : `${currency.symbol}0.00`,
    //   change: 15,
    //   icon: DollarSign,
    //   color: "from-green-400 to-green-600",
    //   chartColor: "#10B981",
    //   format: "currency",
    //   dataKey: "totalGrossSales",
    //   gridClass: "col-span-1",
    //   settledDataKey: "settledGrossSales",
    // },
    {
      title: "Net Sales",
      tooltip:
        "Net revenue amount after seller discounts are deducted.",
      value:
        totals && totals.totalMerchandiseSales
          ? `${currency.symbol}${totals.totalMerchandiseSales?.toLocaleString(
              undefined,
              { minimumFractionDigits: 2, maximumFractionDigits: 2 }
            )}`
          : `${currency.symbol}0.00`,
      settledValue:
        settledTotals && settledTotals.settledMerchandiseSales
          ? `${
              currency.symbol
            }${settledTotals.settledMerchandiseSales?.toLocaleString(
              undefined,
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }
            )}`
          : `${currency.symbol}0.00`,
      change: 15,
      icon: DollarSign,
      color: "from-blue-400 to-blue-600",
      chartColor: "#3B82F6",
      format: "currency",
      dataKey: "totalMerchandiseSales",
      gridClass: "col-span-2",
      settledDataKey: "settledMerchandiseSales",
    },
    {
      title: "Net Profits",
      tooltip:
        "Earnings after all costs and expenses have been deducted from revenue (tax back included in calculations, configure in chart settings).",
      value:
        totals && totals.netProfits
          ? `${currency.symbol}${totals.netProfits?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`
          : `${currency.symbol}0.00`,
      settledValue: settledTotals?.settledNetProfits
        ? `${currency.symbol}${settledTotals.settledNetProfits.toLocaleString(
            undefined,
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }
          )}`
        : `${currency.symbol}0.00`,
      change: -3,
      icon: TrendingUp,
      color: "from-teal-400 to-teal-600",
      chartColor: "#14B8A6",
      format: "currency",
      dataLabel: "~ ESTIMATED",
      dataKey: "netProfits",
      gridClass: "col-span-2",
      settledDataKey: "settledNetProfits",
    },
    {
      title: "Net Margin",
      tooltip:
        "The percentage of revenue that is left after all costs and expenses have been deducted for this product.",
      value: totals?.totalMargin
        ? `${totals.totalMargin.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}%`
        : `0.00%`,
      settledValue: settledTotals?.settledTotalMargin
        ? `${settledTotals.settledTotalMargin.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}%`
        : `0.00%`,
      icon: TrendingUp,
      color: "from-teal-400 to-teal-600",
      chartColor: "#14B8A6",
      format: "currency",
      dataLabel: "~ ESTIMATED",
      dataKey: "totalMargin",
      gridClass: "col-span-3",
      isNotCurrency: true,
      hide: true,
    },
    {
      title: "Cost of Goods",
      tooltip:
        "Direct costs attributable to the production of the goods sold by a company.",
      value:
        totals && totals.costOfGoods
          ? `${currency.symbol}${totals.costOfGoods?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`
          : `${currency.symbol}0.00`,
      settledValue:
        settledTotals && settledTotals.settledCostOfGoods
          ? `${
              currency.symbol
            }${settledTotals.settledCostOfGoods?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`
          : `${currency.symbol}0.00`,
      change: 2,
      icon: Package,
      color: "from-purple-400 to-purple-600",
      chartColor: "#8B5CF6",
      format: "currency",
      dataKey: "costOfGoods",
      gridClass: "col-span-1",
      settledDataKey: "settledCostOfGoods",
    },
    {
      title: "Affiliates",
      tooltip:
        "Estimated fees paid to affiliates for referring customers (incl. ads and partner commissions).",
      value:
        totals && totals.totalEstimatedAffiliateCommission
          ? `${
              currency.symbol
            }${totals.totalEstimatedAffiliateCommission?.toLocaleString(
              undefined,
              { minimumFractionDigits: 2, maximumFractionDigits: 2 }
            )}`
          : `${currency.symbol}0.00`,
      settledValue:
        settledTotals && settledTotals.settledAffiliateCommission
          ? `${
              currency.symbol
            }${settledTotals.settledAffiliateCommission?.toLocaleString(
              undefined,
              { minimumFractionDigits: 2, maximumFractionDigits: 2 }
            )}`
          : `${currency.symbol}0.00`,
      change: 7,
      icon: Percent,
      color: "from-pink-400 to-pink-600",
      chartColor: "#EC4899",
      format: "currency",
      dataLabel: "~ ESTIMATED",
      dataKey: "totalEstimatedAffiliateCommission",
      gridClass: "col-span-1",
      settledDataKey: "settledAffiliateCommission",
    },
    {
      title: "TikTok Commiss. Fee",
      tooltip:
        "Estimation of fees charged by the e-commerce platform for using their services.",
      value:
        totals && totals.totalPlatformFee
          ? `${currency.symbol}${totals.totalPlatformFee?.toLocaleString(
              undefined,
              { minimumFractionDigits: 2, maximumFractionDigits: 2 }
            )}`
          : `${currency.symbol}0.00`,
      settledValue:
        settledTotals && settledTotals.settledPlatformCommission
          ? `${
              currency.symbol
            }${settledTotals.settledPlatformCommission?.toLocaleString(
              undefined,
              { minimumFractionDigits: 2, maximumFractionDigits: 2 }
            )}`
          : `${currency.symbol}0.00`,
      change: 3,
      icon: CreditCard,
      color: "from-orange-400 to-orange-600",
      chartColor: "#F97316",
      format: "currency",
      dataLabel: "~ ESTIMATED",
      dataKey: "totalPlatformFee",
      gridClass: "col-span-1",
      settledDataKey: "settledPlatformCommission",
    },
    // {
    //   title: "FBM Shipping Cost",
    //   tooltip:
    //     "Shipping fee's paid for orders auto-shipped with TikTok, but fulfilled by the seller (Fulfilled by Merchant).",
    //   value:
    //     totals && totals.totalEstimatedFbmShippingCost
    //       ? `${
    //           currency.symbol
    //         }${totals.totalEstimatedFbmShippingCost?.toLocaleString(undefined, {
    //           minimumFractionDigits: 2,
    //           maximumFractionDigits: 2,
    //         })}`
    //       : `${currency.symbol}0.00`,
    //   settledValue:
    //     settledTotals && settledTotals.settledEstimatedFbmShippingCost
    //       ? `${
    //           currency.symbol
    //         }${settledTotals.settledEstimatedFbmShippingCost?.toLocaleString(
    //           undefined,
    //           {
    //             minimumFractionDigits: 2,
    //             maximumFractionDigits: 2,
    //           }
    //         )}`
    //       : `${currency.symbol}0.00`,
    //   change: 3,
    //   icon: Truck,
    //   color: "from-blue-400 to-blue-600",
    //   chartColor: "#3B82F6",
    //   format: "currency",
    //   dataLabel: "~ ESTIMATED",
    //   dataKey: "totalEstimatedFbmShippingCost",
    //   gridClass: "col-span-2",
    //   settledDataKey: "settledEstimatedFbmShippingCost",
    // },
    // {
    //   title: "Seller-Fulfilled Shipping Cost",
    //   tooltip:
    //     "Shipping fee's paid for orders managed, and fulfilled by the seller (you).",
    //   value:
    //     totals && totals.totalEstimatedSellerShippingCost
    //       ? `${
    //           currency.symbol
    //         }${totals.totalEstimatedSellerShippingCost?.toLocaleString(
    //           undefined,
    //           {
    //             minimumFractionDigits: 2,
    //             maximumFractionDigits: 2,
    //           }
    //         )}`
    //       : `${currency.symbol}0.00`,
    //   settledValue: settledTotals?.settledEstimatedSellerShippingCost
    //     ? `${currency.symbol}${settledTotals.settledEstimatedSellerShippingCost.toLocaleString(
    //         undefined,
    //         { minimumFractionDigits: 2, maximumFractionDigits: 2 }
    //       )}`
    //     : `${currency.symbol}0.00`,
    //   change: 3,
    //   icon: Truck,
    //   color: "from-blue-400 to-blue-600",
    //   chartColor: "#3B82F6",
    //   format: "currency",
    //   dataLabel: "~ ESTIMATED",
    //   dataKey: "totalEstimatedSellerShippingCost",
    //   gridClass: "col-span-2",
    //   settledDataKey: "settledEstimatedSellerShippingCost",
    // },
    // {
    //   title: "FBT Fulfillment Fee",
    //   tooltip:
    //     "Fee's paid to TikTok for the fulfillment of orders using Fulfilled by TikTok (FBT). Only applicable for US customers.",
    //   value:
    //     totals && totals.totalEstimatedFbtFulfillmentFee
    //       ? `${
    //           currency.symbol
    //         }${totals.totalEstimatedFbtFulfillmentFee?.toLocaleString(
    //           undefined,
    //           {
    //             minimumFractionDigits: 2,
    //             maximumFractionDigits: 2,
    //           }
    //         )}`
    //       : `${currency.symbol}0.00`,
    //   settledValue:
    //     settledTotals && settledTotals.settledEstimatedFbtFulfillmentFee
    //       ? `${
    //           currency.symbol
    //         }${settledTotals.settledEstimatedFbtFulfillmentFee?.toLocaleString(
    //           undefined,
    //           {
    //             minimumFractionDigits: 2,
    //             maximumFractionDigits: 2,
    //           }
    //         )}`
    //       : `${currency.symbol}0.00`,
    //   change: 3,
    //   icon: Truck,
    //   color: "from-blue-400 to-blue-600",
    //   chartColor: "#3B82F6",
    //   format: "currency",
    //   dataLabel: "~ ESTIMATED",
    //   dataKey: "totalEstimatedFbtFulfillmentFee",
    //   gridClass: "col-span-2",
    //   settledDataKey: "settledEstimatedFbtFulfillmentFee",
    // },
    {
      title: "Net Units Sold",
      tooltip:
        "The total number of units sold excluding cancelled units.",
      value:
        totals && totals.netUnits
          ? totals.netUnits.toString()
          : "0",
      settledValue:
        settledTotals && settledTotals.settledUnits
          ? settledTotals.settledUnits.toString()
          : "0",
      change: 3,
      icon: Truck,
      color: "from-purple-400 to-purple-600",
      chartColor: "#9333EA",
      format: "currency",
      dataLabel: "~ ESTIMATED",
      dataKey: "netUnits",
      gridClass: "col-span-1",
      isNotCurrency: true,
      settledDataKey: "settledUnits",
    },
    {
      title: "Net Seller Discounts",
      tooltip:
        "Net seller discounts applied to the customer payment. Excludes cancelled seller discounts.",
      value:
        totals && totals.totalNetSellerDiscounts
          ? `${currency.symbol}${totals.totalNetSellerDiscounts?.toLocaleString(
              undefined,
              { minimumFractionDigits: 2, maximumFractionDigits: 2 }
            )}`
          : `${currency.symbol}0.00`,
      settledValue:
        settledTotals && settledTotals.settledSellerDiscountAmount
          ? `${
              currency.symbol
            }${settledTotals.settledSellerDiscountAmount?.toLocaleString(
              undefined,
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }
            )}`
          : `${currency.symbol}0.00`,
      change: 15,
      icon: DollarSign,
      color: "from-amber-400 to-amber-600",
      chartColor: "#F59E0B",
      format: "currency",
      dataKey: "totalNetSellerDiscounts",
      gridClass: "col-span-2",
      settledDataKey: "settledSellerDiscountAmount",
    },
    {
      title: "Net Platform Discounts", 
      tooltip:
        "Net platform discounts applied to the customer payment. Excludes cancelled platform discounts.",
      value:
        totals && totals.totalNetPlatformDiscounts
          ? `${currency.symbol}${totals.totalNetPlatformDiscounts?.toLocaleString(
              undefined,
              { minimumFractionDigits: 2, maximumFractionDigits: 2 }
            )}`
          : `${currency.symbol}0.00`,
      settledValue:
        settledTotals && settledTotals.settledPlatformDiscountAmount
          ? `${
              currency.symbol
            }${settledTotals.settledPlatformDiscountAmount?.toLocaleString(
              undefined,
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }
            )}`
          : `${currency.symbol}0.00`,
      change: 15,
      icon: DollarSign,
      color: "from-rose-400 to-rose-600",
      chartColor: "#F43F5E",
      format: "currency",
      dataKey: "totalNetPlatformDiscounts",
      gridClass: "col-span-2",
      settledDataKey: "settledPlatformDiscountAmount",
    },
    // {
    //   title: "FBT Shipping Cost",
    //   tooltip:
    //     "Shipping fee's for orders fulfilled using Fulfilled by TikTok (FBT).",
    //   value:
    //     totals && totals.totalEstimatedFbtShippingCost
    //       ? `${
    //           currency.symbol
    //         }${totals.totalEstimatedFbtShippingCost?.toLocaleString(undefined, {
    //           minimumFractionDigits: 2,
    //           maximumFractionDigits: 2,
    //         })}`
    //       : `${currency.symbol}0.00`,
    //   settledValue:
    //     settledTotals && settledTotals.settledEstimatedFbtShippingCost
    //       ? `${
    //           currency.symbol
    //         }${settledTotals.settledEstimatedFbtShippingCost?.toLocaleString(
    //           undefined,
    //           {
    //             minimumFractionDigits: 2,
    //             maximumFractionDigits: 2,
    //           }
    //         )}`
    //       : `${currency.symbol}0.00`,
    //   change: 3,
    //   icon: Truck,
    //   color: "from-blue-400 to-blue-600",
    //   chartColor: "#3B82F6",
    //   format: "currency",
    //   dataLabel: "~ ESTIMATED",
    //   dataKey: "totalEstimatedFbtShippingCost",
    //   gridClass: "col-span-2",
    //   settledDataKey: "settledEstimatedFbtShippingCost",
    // },
    // {
    //   title: "Tiktok FBT Shipping Cost",
    //   tooltip:
    //     "Shipping fee's for orders fulfilled using Fulfilled by TikTok (FBT).",
    //   value:
    //     totals && totals.totalTiktokEstimatedFbtShippingCost
    //       ? `${
    //           currency.symbol
    //         }${totals.totalTiktokEstimatedFbtShippingCost?.toLocaleString(undefined, {
    //           minimumFractionDigits: 2,
    //           maximumFractionDigits: 2,
    //         })}`
    //       : `${currency.symbol}0.00`,
    //   settledValue:
    //     settledTotals && settledTotals.settledEstimatedFbtShippingCost
    //       ? `${
    //           currency.symbol
    //         }${settledTotals.settledEstimatedFbtShippingCost?.toLocaleString(
    //           undefined,
    //           {
    //             minimumFractionDigits: 2,
    //             maximumFractionDigits: 2,
    //           }
    //         )}`
    //       : `${currency.symbol}0.00`,
    //   change: 3,
    //   icon: Truck,
    //   color: "from-blue-400 to-blue-600",
    //   chartColor: "#3B82F6",
    //   format: "currency",
    //   dataLabel: "~ ESTIMATED",
    //   dataKey: "totalTiktokEstimatedFbtShippingCost",
    //   gridClass: "col-span-2",
    //   settledDataKey: "settledEstimatedFbtShippingCost",
    // },
  ].filter(tile => {
    if (!overrideTiles) return true;
    const override = overrideTiles.find(o => o.dataKey === tile.dataKey);
    if (!override) return false;
    tile.hide = override.hide;
    tile.color = override.color || tile.color;
    tile.chartColor = override.chartColor || tile.chartColor;
    tile.gridClass = override.gridClass;
    return true;
  });

  return (
    <div className="space-y-2">
      {/* Date Range Filter and Sales Overview Title */}
      <div className="mb-4 flex justify-between gap-4 items-center">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold text-foreground">
            Sales Overview
          </h3>
          <div className="flex items-center gap-2">
            {headerExtra}
            <Switch
              checked={swapChartsOnDashboard}
              onCheckedChange={setSwapChartsOnDashboard}
            />
            <span className="text-sm text-muted-foreground">
              Settled Chart Mode
            </span>
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger>
                  <Info size={14} className="text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent className="text-lg w-[300px]">
                  <p>
                    Switch to settled chart mode to see completed (settled) orders
                    and future projections based on live & estimate data.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {extraRightHeader && (extraRightHeader)}
        </div>
      </div>

      {/* Dashboard Tiles */}
      <div className="pb-10 grid grid-cols-4 gap-4">
        {dashboardTiles.map((tile, index) => (
          tile.hide ? null : (
            <ChartTileOverview
            key={index}
            tile={tile}
            swapCharts={swapChartsOnDashboard}
            isLoading={isLoading}
            isSettledLoading={isSettledLoading}
            combinedData={combinedData}
          />)
        ))}
      </div>
    </div>
  );
}
